import { reactive, watch, watchEffect } from 'vue';
import BasePagedListFilterService from '@/services/BasePagedListFilterService';
import TempWorkerPagedListFilter from '@/components/temp-worker/TempWorkerPagedListFilter.vue';
import AgencyCandidateApiAgency from '@/api/AgencyCandidateApiAgency';
import router from '@/router/index.router';
import OrderUserApiAgency from '@/api/OrderUserApiAgency';
import type OrderEntity from '@/entity/order/OrderEntity';
import { USER_STATUS } from '@/typings/enums/USER_STATUS';

export class TempWorkerPagedListFilterService
	extends BasePagedListFilterService<
	Armado.TempWorkerPagedListParametersExtended,
	(Armado.TempWorkerListItemEntityType | IOrderUserAgencyListItemModel)
	> {

	protected get api () {
		return this.isInOrderContext
			? OrderUserApiAgency
			: AgencyCandidateApiAgency;
	}

	private readonly state = reactive({ inModal: false });
	private order?: OrderEntity;

	get inModal () { return this.state.inModal; }
	get quickFilters () { return undefined; }
	get listFilterComponent () { return this.filterComponent; }
	get isInOrderContext () { return router.currentRoute.value.name === 'OrderDetails' && !this.state.inModal; }

	readonly filterComponent = TempWorkerPagedListFilter;

	filterProps = {
		aside: true,
		inModal: this.state.inModal,
	};

	filter = reactive<Armado.TempWorkerPagedListParametersExtended>({
		...this.commonFilter,
		dateRange: {},
		rating: undefined,
		to: undefined,
		from: undefined,
		showRejected: false,
		status: undefined,
		numberOfContractsGte: undefined,
		agencyCompanyId: undefined,
		userAgencyQualifications: [],
		professionalSkillIds: [],
		orderProfessionalSkillsProfessionalSkillIds: [],
		excludeOrderId: undefined,
		languageIds: [],
		talentPoolIds: [] as number[],
		// showRefused: null,
		hasPersonalVehicle: false,
		mobility: undefined,
		orderId: undefined,
		order: {
			property: 'Id',
			ascending: false,
		},
	});


	constructor () {
		super();

		watch(() => router.currentRoute.value, () => {
			this.filter.orderId = router.currentRoute.value.name === 'OrderDetails'
				? +router.currentRoute.value.params.id
				: undefined;
		});

		watchEffect(() => {
			this.filter.from = this.filter.dateRange?.start?.toPost(true);
			this.filter.to = this.filter.dateRange?.end?.toPost(true);
		});
	}


	setInModal (isInModal: boolean) {
		this.state.inModal = isInModal;
	}

	setOrderEntity (order?: OrderEntity) {
		this.order = order;
		if (order) {
			this.filter.professionalSkillIds = order?.professionalSkill?.id ? [order?.professionalSkill?.id] : [];
			this.filter.orderProfessionalSkillsProfessionalSkillIds = order?.orderProfessionalSkillsProfessionalSkillIds;
			this.filter.agencyCompanyId = order?.agencyCompanyId ?? undefined;
			this.filter.languageIds = order?.languages.mapKey('id') ?? null;
			this.filter.excludeOrderId = order?.id;
			this.filter.dateRange = {
				start: order.start,
				end: order.end,
			};
			this.filter.from = order.start.toPost(true);
			this.filter.to = order.end.toPost(true);
		}
	}

	override async hookBeforeApplyFilterAsync () {
		if (this.order) {
			this.filter.excludeOrderId = this.order?.id;
			this.filter.status = USER_STATUS.Activated;
		}
	}

	initFilter (props: Object & { talentPoolId?: number}) {
		Object.assign(this.filter, {
			...this.filter,
			...props,
			talentPoolIds: props.talentPoolId ? [props.talentPoolId] : undefined,
		});
	}
}

const tempWorkerPagedListFilterServiceSingleton = new TempWorkerPagedListFilterService();

export default function useTempWorkerPagedListFilterService () {
	return tempWorkerPagedListFilterServiceSingleton;
}
